import { ChangeDetectionStrategy, Component, ViewChild, inject } from "@angular/core";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { TableComponent, TableComponent as TableComponent_1 } from "@components/table/table.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { uniqBy } from "lodash";
import { Subject } from "rxjs";
import { RaskColumn, RaskColumnButtonTypes } from "~app/common/components/table/models/rask-column";
import { RaskDataChanged } from "~app/common/components/table/models/rask-data-changed";

import { RaskRoller } from "~app/common/models/constants";
import { RedovisningshanvisningDto } from "~app/common/models/styrtabeller/RedovisningshanvisningDto";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Viol3formatService } from "~app/features/shared/services/viol3format.service";
import { ExtraHsRhViewModel, StyrtabellRedovisningshanvisningHandelssortimentDto, StyrtabellRedovisningshanvisningViewModel } from "../../regelverkViol3.models";
import { RegelverkViol3Service } from "../../regelverkViol3.service";

@Component({
    selector: "rask-redovisningshanvisning-extra-dialog",
    templateUrl: "./redovisningshanvisning-extra-dialog.component.html",
    styleUrls: ["./redovisningshanvisning-extra-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ActionButtonComponent,
        TableComponent_1,
        ButtonComponent
    ]
})
export class RedovisningshanvisningExtraDialogComponent {
    modal = inject(NgbActiveModal);
    @ViewChild(TableComponent) tableComp: TableComponent;

    protected allRedovisningshanvisningar: RedovisningshanvisningDto[] = [];
    protected colDef: RaskColumn[];
    protected extraHsRh: ExtraHsRhViewModel[];
    protected isDirty = false;
    protected nyRedovisningshanvisning: RedovisningshanvisningDto;
    protected nyttInmatningshandelssortiment: string;
    protected hasRoleProduktAdmin = false;

    private readonly regelverkViol3Service = inject(RegelverkViol3Service);

    private current: StyrtabellRedovisningshanvisningViewModel;
    private readonly authenticationService = inject(AuthenticationService);

    set data(value: any) {
        this.hasRoleProduktAdmin = this.authenticationService.hasRole(RaskRoller.role.Produktadministrera);
        this.initValues(value);
    }

    protected add(): void {
        this.extraHsRh.push(new ExtraHsRhViewModel(null, null));
        this.tableComp.refreshGrid();
        this.isDirty = true;
    }

    protected close(): void {
        this.modal.close(this.isDirty ? this.mapFromViewModel(this.extraHsRh) : null);
    }

    protected delete(row: StyrtabellRedovisningshanvisningHandelssortimentDto): void {
        const idx = this.extraHsRh.findIndex(x => x.guid === row.guid);
        if (idx > -1) {
            this.extraHsRh.splice(idx, 1);
            this.tableComp.refreshGrid();
            this.isDirty = true;
        }
    }

    protected formatter = (v: RedovisningshanvisningDto) => v;

    protected onChange = ($event) => { this.isDirty = true; };

    protected viewFormatter = (v: RedovisningshanvisningDto) => v.identitet;

    private initValues(inputData: any): void {
        this.extraHsRh = this.mapToViewModel(inputData?.rh?.styrtabellRedovisningshanvisningar);
        this.current = inputData.rh as StyrtabellRedovisningshanvisningViewModel;
        this.allRedovisningshanvisningar = inputData.allrh;
        const dataChanged = new Subject<RaskDataChanged>();
        dataChanged.subscribe(x => {
            this.isDirty = true;
        });
        this.setupColumnDef(dataChanged);
    }

    private mapFromViewModel(vm: ExtraHsRhViewModel[]): StyrtabellRedovisningshanvisningHandelssortimentDto[] {
        return uniqBy(vm.map(v => ({
            guid: v.guid,
            inmatningshandelssortiment: v.inmatningshandelssortiment,
            redovisningshanvisning: v.redovisningshanvisning
        })),
        x => `${x.inmatningshandelssortiment}_${x.redovisningshanvisning?.identitet}`);
    }

    private mapToViewModel(dto: StyrtabellRedovisningshanvisningHandelssortimentDto[]): ExtraHsRhViewModel[] {
        return ObjectUtils.hasValue(dto) ?
            dto.filter(x => ObjectUtils.hasValue(x.inmatningshandelssortiment))
                .map(d => new ExtraHsRhViewModel(d.inmatningshandelssortiment, d.redovisningshanvisning, d.guid))
            : [];
    }

    private setupColumnDef(dataChanged: Subject<RaskDataChanged>) {
        this.colDef = [
            {
                disabled: !this.hasRoleProduktAdmin,
                field: " ",
                type: "button",
                onClick: (ex: StyrtabellRedovisningshanvisningHandelssortimentDto) => this.delete(ex),
                filter: false,
                text: "",
                minWidth: 30,
                maxWidth: 30,
                buttonType: RaskColumnButtonTypes.delete
            },
            {
                headerName: "Inmätningshandelssortiment*",
                field: "inmatningshandelssortiment",
                filter: true,
                filterParams: {
                    filterOptions: ["contains"],
                    maxNumConditions: 1
                },
                editable: this.hasRoleProduktAdmin,
                sortable: true,
                minWidth: 100,
                maxWidth: 200,
                dataChanged: dataChanged
            },
            {
                headerName: "RH*",
                field: "redovisningshanvisning",
                type: "selector",
                editable: this.hasRoleProduktAdmin,
                filter: "agTextColumnFilter",
                filterParams: {
                    filterOptions: ["contains"],
                    maxNumConditions: 1,
                    textMatcher: ({ data, filterText }) => {
                        const value =  `${data.redovisningshanvisning.identitet}: ${data.redovisningshanvisning.namn}`;
                        return value.toLowerCase().indexOf(filterText.toLowerCase()) >= 0;
                    }
                },
                sortable: true,
                sortComparator: (valueA: RedovisningshanvisningDto, valueB: RedovisningshanvisningDto) => {
                    if (valueA?.identitet === valueB?.identitet) {
                        return 0;
                    }
                    return (valueA?.identitet > valueB?.identitet) ? 1 : -1;
                },
                initialWidth: 200,
                minWidth: 250,
                values: (args:
                    {
                        mottagningsplats: { id: number };
                        inmatningshandelssortiment: string;
                        befraktare: any;
                        redovisningshanvisning: { id: number };
                        transportslag: string;
                        anskaffningsformer: string[];
                    }) =>
                    this.regelverkViol3Service.getFilteredRedovisningshanvisningar(
                        this.current.anskaffningsform,
                        this.current.mottagningsplats.id,
                        this.current.befraktare,
                        args.inmatningshandelssortiment,
                        this.current.transportslag,
                        this.current.kontraktstyp),
                viewFormatter: (arg) => arg ? `${arg.identitet}: ${arg.namn}` : undefined,
                formatter: (arg) => arg,
                tooltipGenerator: (row: StyrtabellRedovisningshanvisningViewModel) => Viol3formatService.formatRedovisninghanvisningTooltip(row.redovisningshanvisning),
                dataChanged: dataChanged
            }
        ] as unknown as RaskColumn[];
    }
}
